import type {
  Account,
  AccountStreamMatch,
  ActivityFeedItem,
  ICP,
  Product,
  ReviewItem,
  Signal,
  Stream,
} from "../../types/accountIntelligence";

// ─── Products ────────────────────────────────────────────────────────────────

export const MOCK_PRODUCTS: Product[] = [
  {
    id: "p1",
    name: "AI Visual Inspection System",
    description:
      "Machine-vision platform that detects surface defects, dimensional deviations, and assembly errors on production lines in real time.",
    capabilityTags: ["Computer Vision", "Defect Detection", "Quality Control", "Edge AI"],
    useCases: [
      "Automated surface defect detection on stamped metal parts",
      "Dimensional inspection of precision-machined components",
      "Assembly verification for wiring harnesses",
    ],
    valueProposition:
      "Reduce scrap rates by up to 40% and eliminate manual inspection bottlenecks on high-volume production lines.",
    relevantSystems: ["MES", "SCADA", "ERP", "Robotic assembly lines"],
    exclusions: "Pharmaceutical, food & beverage, non-manufacturing environments",
  },
  {
    id: "p2",
    name: "Predictive Maintenance Analytics",
    description:
      "IoT-connected sensor platform that monitors CNC machines, conveyors, and rotating equipment to predict failures before they occur.",
    capabilityTags: ["Predictive Maintenance", "IoT", "Anomaly Detection", "Asset Health"],
    useCases: [
      "Spindle failure prediction for CNC machining centres",
      "Conveyor gearbox degradation monitoring",
      "Compressor health tracking",
    ],
    valueProposition:
      "Cut unplanned downtime by 60% and extend equipment lifetime through condition-based maintenance.",
    relevantSystems: ["CNC controllers", "SCADA", "CMMS", "OPC-UA networks"],
    exclusions: "Office environments, non-industrial machinery",
  },
];

// ─── ICPs ─────────────────────────────────────────────────────────────────────

export const MOCK_ICPS: ICP[] = [
  {
    id: "icp1",
    name: "European Automotive Component Manufacturers",
    accountTypes: ["Tier-1 Supplier", "Tier-2 Supplier"],
    industries: ["Automotive", "Metal Stamping", "Plastics & Composites"],
    geography: ["Germany", "France", "Czech Republic", "Poland", "Austria"],
    companySize: "200–5,000 employees",
    buyingContext:
      "Manufacturers facing customer quality mandates, increasing scrap rates, or capacity expansion projects.",
    exclusions: "OEMs, dealerships, aftermarket distributors",
  },
  {
    id: "icp2",
    name: "Aerospace Tier-1 Suppliers",
    accountTypes: ["Tier-1 Supplier", "MRO Provider"],
    industries: ["Aerospace & Defense", "Precision Engineering"],
    geography: ["France", "Germany", "UK", "Spain", "Netherlands"],
    companySize: "500–10,000 employees",
    buyingContext:
      "Suppliers under OEM quality programmes (AS9100) investing in digital quality or maintenance optimisation.",
    exclusions: "Defense-only contractors without commercial aviation exposure",
  },
];

// ─── Signals (shared pool) ────────────────────────────────────────────────────

export const MOCK_SIGNALS: Signal[] = [
  {
    id: "s1",
    type: "hiring",
    title: "Hiring: Automation Engineers",
    description:
      "Posted 8 open roles for automation and robotics engineers across three plants in Bavaria.",
    date: "2026-04-28",
    source: "LinkedIn Jobs",
    relevance: "high",
    confidence: "high",
    accountId: "a1",
    relatedAngleId: "ang1",
  },
  {
    id: "s2",
    type: "investment",
    title: "New production line investment – €45M CapEx",
    description:
      "Annual report references a €45M investment in a new body-panel stamping line at the Regensburg facility.",
    date: "2026-04-15",
    source: "Annual Report 2025",
    relevance: "high",
    confidence: "high",
    accountId: "a1",
    relatedAngleId: "ang1",
  },
  {
    id: "s3",
    type: "event",
    title: "Smart Factory Expo participation",
    description:
      "Exhibiting at Smart Factory Expo Munich 2026; theme: digital quality and zero-defect manufacturing.",
    date: "2026-03-10",
    source: "Trade Show Database",
    relevance: "high",
    confidence: "medium",
    accountId: "a1",
    relatedAngleId: "ang1",
  },
  {
    id: "s4",
    type: "partnership",
    title: "Partnership with KUKA Robotics announced",
    description:
      "Press release: multi-year integration partnership for robotic welding cells across French sites.",
    date: "2026-04-02",
    source: "Company Press Release",
    relevance: "high",
    confidence: "high",
    accountId: "a2",
    relatedAngleId: "ang2",
  },
  {
    id: "s5",
    type: "expansion",
    title: "Facility expansion – new plant in Lodz",
    description:
      "Confirmed 12,000 m² greenfield expansion in Poland targeting EV battery component production.",
    date: "2026-03-22",
    source: "Industry Press",
    relevance: "medium",
    confidence: "high",
    accountId: "a2",
    relatedAngleId: "ang2",
  },
  {
    id: "s6",
    type: "hiring",
    title: "Hiring: Quality Engineering Director",
    description:
      "Senior Quality Engineering Director role posted, reporting to VP Manufacturing – focus on zero-defect initiatives.",
    date: "2026-04-30",
    source: "LinkedIn Jobs",
    relevance: "high",
    confidence: "high",
    accountId: "a3",
    relatedAngleId: "ang3",
  },
  {
    id: "s7",
    type: "contract",
    title: "New OEM contract – BMW iDrive programme",
    description:
      "Awarded supplier contract for precision brackets in the BMW iDrive 2027 programme; strict PPM targets cited.",
    date: "2026-04-18",
    source: "Trade Publication",
    relevance: "high",
    confidence: "medium",
    accountId: "a3",
    relatedAngleId: "ang3",
  },
  {
    id: "s8",
    type: "investment",
    title: "MRO digitisation programme launched",
    description:
      "Internal programme announced to digitalise maintenance records and transition to CBM across 4 facilities.",
    date: "2026-04-05",
    source: "Company Intranet / Industry Press",
    relevance: "high",
    confidence: "high",
    accountId: "a4",
    relatedAngleId: "ang4",
  },
  {
    id: "s9",
    type: "hiring",
    title: "Hiring: Reliability Engineers",
    description:
      "Batch of reliability and maintenance engineer roles posted at Toulouse and Bremen sites.",
    date: "2026-04-25",
    source: "LinkedIn Jobs",
    relevance: "high",
    confidence: "high",
    accountId: "a4",
    relatedAngleId: "ang4",
  },
  {
    id: "s10",
    type: "event",
    title: "MRO Europe conference participation",
    description: "Speaking slot at MRO Europe on CBM strategies for aerostructure components.",
    date: "2026-03-18",
    source: "Conference Programme",
    relevance: "medium",
    confidence: "high",
    accountId: "a4",
    relatedAngleId: "ang4",
  },
  {
    id: "s11",
    type: "regulatory",
    title: "AS9100 Rev D recertification audit",
    description:
      "Announced AS9100 Rev D audit scope expansion to include digital quality records across all sites.",
    date: "2026-04-10",
    source: "Certification Body Bulletin",
    relevance: "medium",
    confidence: "medium",
    accountId: "a5",
  },
  {
    id: "s12",
    type: "investment",
    title: "€8M quality infrastructure investment",
    description: "Board approved €8M capex for quality lab upgrades and measurement equipment.",
    date: "2026-02-14",
    source: "Annual Report",
    relevance: "medium",
    confidence: "medium",
    accountId: "a5",
  },
];

// ─── Accounts ─────────────────────────────────────────────────────────────────

export const MOCK_ACCOUNTS: Account[] = [
  {
    id: "a1",
    name: "Bauer Automotive Systeme GmbH",
    industry: "Automotive – Tier-1 Stamping",
    geography: "Regensburg, Germany",
    size: "1,800 employees",
    accountType: "Tier-1 Supplier",
    website: "bauer-automotive.de",
    profile: {
      description:
        "Bauer Automotive Systeme is a Tier-1 supplier of precision-stamped steel body panels and structural brackets for European OEMs. The company operates four dedicated press lines in Regensburg with combined annual throughput of approximately 18 million parts. Primary customers include BMW Group and Mercedes-Benz AG.",
      founded: "1971",
      estimatedRevenue: "€420M (est.)",
      technologies: ["Progressive die stamping", "Transfer pressing", "Laser welding", "Siemens SINUMERIK CNC", "SAP ME"],
      notableCustomers: ["BMW Group", "Mercedes-Benz AG", "Continental AG"],
      certifications: ["IATF 16949", "ISO 14001", "ISO 45001"],
      hqAddress: "Bajuwarenstraße 12, 93053 Regensburg, Germany",
    },
  },
  {
    id: "a2",
    name: "Groupe Mercier Industries",
    industry: "Automotive – Metal Forming",
    geography: "Lyon, France",
    size: "3,200 employees",
    accountType: "Tier-1 Supplier",
    website: "mercier-industries.fr",
    profile: {
      description:
        "Groupe Mercier Industries is a French Tier-1 automotive metal forming group with operations in France, Germany, and Poland. The group specialises in complex structural stampings and EV battery enclosures, serving Stellantis and Renault through long-term supply agreements. The Lodz greenfield plant targets battery bracket production at scale.",
      founded: "1958",
      estimatedRevenue: "€780M (est.)",
      technologies: ["Multi-stage progressive stamping", "Hydroforming", "Robotic welding (KUKA)", "Renault DELMIA MES"],
      notableCustomers: ["Stellantis", "Renault Group", "Valeo"],
      certifications: ["IATF 16949", "ISO 50001"],
      hqAddress: "14 Rue de l'Industrie, 69120 Vaulx-en-Velin, France",
    },
  },
  {
    id: "a3",
    name: "Volný Precision Components s.r.o.",
    industry: "Automotive – Precision Machining",
    geography: "Brno, Czech Republic",
    size: "620 employees",
    accountType: "Tier-2 Supplier",
    website: "volny-precision.cz",
    profile: {
      description:
        "Volný Precision Components is a Czech Tier-2 CNC machining specialist producing precision structural brackets, housings, and transmission components for Tier-1 and OEM customers across Central Europe. The company recently secured a direct supply contract for the BMW iDrive 2027 programme, marking its transition to Tier-1 engagement.",
      founded: "1993",
      estimatedRevenue: "€68M (est.)",
      technologies: ["5-axis CNC machining", "Heidenhain TNC", "CMM quality inspection", "EDM"],
      notableCustomers: ["BMW Group (direct)", "Bauer Automotive Systeme GmbH", "Magna International"],
      certifications: ["IATF 16949", "ISO 9001"],
      hqAddress: "Průmyslová 44, 619 00 Brno, Czech Republic",
    },
  },
  {
    id: "a4",
    name: "Airframe Solutions SAS",
    industry: "Aerospace – Aerostructures",
    geography: "Toulouse, France",
    size: "4,100 employees",
    accountType: "Tier-1 Supplier",
    website: "airframesolutions.fr",
    profile: {
      description:
        "Airframe Solutions SAS is a Tier-1 aerostructures manufacturer supplying fuselage panels, wing ribs, and pylon structures to Airbus. The group operates primary facilities in Toulouse (aerostructures) and Bremen (systems integration) under a unified AS9100-certified quality system. An internal CBM digitalisation programme is currently underway across both facilities.",
      founded: "1986",
      estimatedRevenue: "€1.1B (est.)",
      technologies: ["5-axis machining (DMG Mori)", "Automated fibre placement", "OPC-UA", "SAP PM (CMMS)", "Siemens SIMATIC"],
      notableCustomers: ["Airbus (A320, A350, A380 programmes)", "Safran Landing Systems"],
      certifications: ["AS9100 Rev D", "EN 9110", "NADCAP (machining, welding)"],
      hqAddress: "12 Allée des Forges, 31300 Toulouse, France",
    },
  },
  {
    id: "a5",
    name: "Engelhoff Composite Technologies AG",
    industry: "Aerospace – Composites",
    geography: "Hamburg, Germany",
    size: "950 employees",
    accountType: "Tier-1 Supplier",
    website: "engelhoff-composites.de",
    profile: {
      description:
        "Engelhoff Composite Technologies AG develops and manufactures carbon-fibre composite structures for aerospace and defence applications. Products include nacelle components, fairings, and interior structural panels. The company supplies Airbus and Rolls-Royce from its Hamburg facility, with a satellite composites lab in Augsburg.",
      founded: "2002",
      estimatedRevenue: "€195M (est.)",
      technologies: ["Prepreg layup", "Autoclave curing", "RTM", "NDT (ultrasonic)", "Automated tape laying"],
      notableCustomers: ["Airbus", "Rolls-Royce", "Lufthansa Technik"],
      certifications: ["AS9100 Rev D", "NADCAP (composites)", "EN 9110"],
      hqAddress: "Composites-Allee 7, 21129 Hamburg, Germany",
    },
  },
  {
    id: "a6",
    name: "Krantz Automotive Plastics GmbH",
    industry: "Automotive – Injection Moulding",
    geography: "Stuttgart, Germany",
    size: "1,100 employees",
    accountType: "Tier-2 Supplier",
    website: "krantz-plastics.de",
  },
  {
    id: "a7",
    name: "Société Forgeage Laval",
    industry: "Automotive – Forging",
    geography: "Laval, France",
    size: "480 employees",
    accountType: "Tier-2 Supplier",
    website: "forgeage-laval.fr",
  },
  {
    id: "a8",
    name: "Brno Engineering Works a.s.",
    industry: "Automotive – General Manufacturing",
    geography: "Brno, Czech Republic",
    size: "2,300 employees",
    accountType: "Tier-1 Supplier",
    website: "bew.cz",
  },
];

// ─── Account-Stream Matches ───────────────────────────────────────────────────

export const MOCK_ACCOUNT_STREAM_MATCHES: AccountStreamMatch[] = [
  // Stream 1 – AI Visual Inspection × European Automotive Manufacturers
  {
    id: "m1",
    account: MOCK_ACCOUNTS[0], // Bauer
    streamId: "str1",
    status: "engaged",
    fitScore: "high",
    fitRationale:
      "High-volume stamped metal part production is the primary use case for AI visual inspection. Bauer operates 4 press lines with known quality audit pressure from BMW.",
    lastReviewed: "2026-04-29",
    topAngle: {
      id: "ang1",
      title: "Production Line Modernisation & Zero-Defect Drive",
      status: "actionable",
      rationale:
        "Bauer is investing €45M in a new stamping line while under BMW quality mandates. This creates a clear window for automated inspection technology.",
      fitBasis:
        "High-volume metal stamping with strict OEM PPM requirements is the primary use case.",
      timingBasis:
        "CapEx announcement, automation hiring, and Smart Factory event participation all point to an active investment cycle.",
      supportingSignals: [MOCK_SIGNALS[0], MOCK_SIGNALS[1], MOCK_SIGNALS[2]],
      confidence: "high",
      nextStep:
        "Initiate contact with Head of Manufacturing Engineering. Reference BMW quality programme context.",
    },
    angles: [
      {
        id: "ang1",
        title: "Production Line Modernisation & Zero-Defect Drive",
        status: "actionable",
        rationale:
          "Bauer is investing €45M in a new stamping line while under BMW quality mandates. This creates a clear window for automated inspection technology.",
        fitBasis:
          "High-volume metal stamping with strict OEM PPM requirements is the primary use case.",
        timingBasis:
          "CapEx announcement, automation hiring, and Smart Factory event participation all point to an active investment cycle.",
        supportingSignals: [MOCK_SIGNALS[0], MOCK_SIGNALS[1], MOCK_SIGNALS[2]],
        confidence: "high",
        nextStep:
          "Initiate contact with Head of Manufacturing Engineering. Reference BMW quality programme context.",
      },
    ],
    signals: [MOCK_SIGNALS[0], MOCK_SIGNALS[1], MOCK_SIGNALS[2]],
    fitRationaleSections: [
      {
        label: "Account Type Fit",
        fitLevel: "high",
        explanation:
          "Tier-1 automotive stamping supplier — the core ICP account type. Operates dedicated press lines producing precision metal body panels.",
      },
      {
        label: "Domain Fit",
        fitLevel: "high",
        explanation:
          "Automotive metal stamping involves high-volume, repetitive production of components with tight surface quality tolerances — exactly the environment where vision-based inspection delivers the highest ROI.",
      },
      {
        label: "Geography Fit",
        fitLevel: "high",
        explanation:
          "Headquarters and primary plant in Regensburg, Germany. Fully within the ICP's target geography.",
      },
      {
        label: "Use Case Fit",
        fitLevel: "high",
        explanation:
          "Produces stamped body panels and structural brackets requiring 100% surface inspection. Current process relies on manual sampling, which creates quality escape risk.",
      },
      {
        label: "Process / System Fit",
        fitLevel: "medium",
        explanation:
          "Runs a Siemens SINUMERIK CNC and SCADA environment. MES is SAP ME. Integration with the user's platform is achievable via OPC-UA.",
      },
      {
        label: "Ecosystem Role",
        fitLevel: "high",
        explanation:
          "Direct supplier to BMW and Mercedes-Benz. OEM quality pressure is a strong buying trigger for inspection investment.",
      },
    ],
    ecosystem: [
      {
        relationship: "Customer",
        entity: "BMW Group",
        whyItMatters: "Primary OEM customer; quality mandates drive inspection investment.",
      },
      {
        relationship: "Customer",
        entity: "Mercedes-Benz AG",
        whyItMatters: "Secondary OEM customer; shared quality compliance pressure.",
      },
      {
        relationship: "Integration Partner",
        entity: "KUKA Robotics",
        whyItMatters: "Existing robotic cell integration; inspection can be mounted inline.",
      },
      {
        relationship: "Peer Supplier",
        entity: "Krantz Automotive Plastics GmbH",
        whyItMatters: "Shares Tier-2 supplier network; indicates ecosystem familiarity.",
      },
    ],
    decisions: [
      {
        date: "2026-04-10",
        actor: "agent",
        action: "Candidate Added",
        description:
          "Agent identified Bauer as a high-fit candidate based on manufacturing footprint and BMW supplier relationship.",
      },
      {
        date: "2026-04-12",
        actor: "user",
        action: "Accepted as Target Account",
        description: "User confirmed account as Target Account and moved it to Active Watch.",
      },
      {
        date: "2026-04-28",
        actor: "agent",
        action: "State Change Recommended",
        description:
          "Agent detected CapEx announcement and automation hiring signals. Recommended move to Trigger Detected.",
      },
      {
        date: "2026-04-29",
        actor: "user",
        action: "Promoted to Engagement Window",
        description:
          "User reviewed signals and promoted account to Engagement Window. Note: timing aligns with Smart Factory Munich in June.",
      },
    ],
  },
  {
    id: "m2",
    account: MOCK_ACCOUNTS[1], // Groupe Mercier
    streamId: "str1",
    status: "watching",
    fitScore: "high",
    fitRationale:
      "Large Tier-1 metal forming supplier with EV expansion activity. High structural fit; timing signals emerging from robotics partnership.",
    lastReviewed: "2026-04-20",
    topAngle: {
      id: "ang2",
      title: "EV Battery Component Line – Quality at Scale",
      status: "developing",
      rationale:
        "Mercier is expanding into EV battery bracket production, a high-precision segment with strict OEM yield requirements.",
      fitBasis:
        "Metal forming at scale with expanding EV programme fits the core visual inspection use case.",
      timingBasis:
        "New robotics partnership and greenfield plant expansion signal an active investment cycle.",
      supportingSignals: [MOCK_SIGNALS[3], MOCK_SIGNALS[4]],
      confidence: "medium",
      nextStep:
        "Monitor for additional CapEx or quality programme signals. Consider outreach if a third signal appears.",
    },
    angles: [
      {
        id: "ang2",
        title: "EV Battery Component Line – Quality at Scale",
        status: "developing",
        rationale:
          "Mercier is expanding into EV battery bracket production, a high-precision segment with strict OEM yield requirements.",
        fitBasis:
          "Metal forming at scale with expanding EV programme fits the core visual inspection use case.",
        timingBasis:
          "New robotics partnership and greenfield plant expansion signal an active investment cycle.",
        supportingSignals: [MOCK_SIGNALS[3], MOCK_SIGNALS[4]],
        confidence: "medium",
        nextStep:
          "Monitor for additional CapEx or quality programme signals. Consider outreach if a third signal appears.",
      },
    ],
    signals: [MOCK_SIGNALS[3], MOCK_SIGNALS[4]],
    fitRationaleSections: [
      {
        label: "Account Type Fit",
        fitLevel: "high",
        explanation:
          "Large Tier-1 metal forming supplier with multi-site operations across France and Eastern Europe.",
      },
      {
        label: "Domain Fit",
        fitLevel: "high",
        explanation:
          "Metal forming and stamping for automotive OEMs. EV battery brackets are a growing segment requiring high dimensional accuracy.",
      },
      {
        label: "Geography Fit",
        fitLevel: "high",
        explanation: "Headquartered in Lyon with Polish expansion — both within ICP geography.",
      },
      {
        label: "Use Case Fit",
        fitLevel: "medium",
        explanation:
          "Current focus on structural body components. EV battery brackets are a new programme where quality is critical from day one.",
      },
      {
        label: "Process / System Fit",
        fitLevel: "medium",
        explanation:
          "Operates a mix of legacy and modern press equipment. New greenfield plant likely to have modern integration capabilities.",
      },
      {
        label: "Ecosystem Role",
        fitLevel: "high",
        explanation:
          "Tier-1 to Stellantis and Renault. OEM quality pressure increasing as EV programmes ramp.",
      },
    ],
    ecosystem: [
      {
        relationship: "Customer",
        entity: "Stellantis",
        whyItMatters: "Primary OEM customer for body panel components.",
      },
      {
        relationship: "Customer",
        entity: "Renault Group",
        whyItMatters: "EV programme customer; quality requirements tightening.",
      },
      {
        relationship: "Integration Partner",
        entity: "KUKA Robotics",
        whyItMatters: "New multi-year robotics partnership — integration opportunity.",
      },
    ],
    decisions: [
      {
        date: "2026-04-05",
        actor: "agent",
        action: "Candidate Added",
        description: "Agent identified Mercier as a high-fit candidate based on ICP match.",
      },
      {
        date: "2026-04-07",
        actor: "user",
        action: "Accepted as Target Account",
        description: "User confirmed and placed in Active Watch.",
      },
      {
        date: "2026-04-20",
        actor: "agent",
        action: "Trigger Detected",
        description:
          "KUKA partnership and Polish plant expansion signals detected. State changed to Trigger Detected.",
      },
    ],
  },
  {
    id: "m3",
    account: MOCK_ACCOUNTS[2], // Volný Precision
    streamId: "str1",
    status: "watching",
    fitScore: "high",
    fitRationale:
      "Precision machining supplier with new BMW contract and Quality Director hiring. Strong timing signals.",
    lastReviewed: undefined,
    topAngle: {
      id: "ang3",
      title: "New Programme Ramp – BMW iDrive Quality Requirements",
      status: "actionable",
      rationale:
        "Volný won a BMW contract with explicit PPM targets. Hiring a Quality Engineering Director signals proactive quality investment.",
      fitBasis: "Precision machined brackets for automotive OEM programmes.",
      timingBasis:
        "New OEM contract + quality leadership hiring = active decision-making on quality infrastructure.",
      supportingSignals: [MOCK_SIGNALS[5], MOCK_SIGNALS[6]],
      confidence: "high",
      nextStep:
        "High urgency. Reach out to incoming Quality Engineering Director or Manufacturing VP before headcount is in place.",
    },
    angles: [
      {
        id: "ang3",
        title: "New Programme Ramp – BMW iDrive Quality Requirements",
        status: "actionable",
        rationale:
          "Volný won a BMW contract with explicit PPM targets. Hiring a Quality Engineering Director signals proactive quality investment.",
        fitBasis: "Precision machined brackets for automotive OEM programmes.",
        timingBasis:
          "New OEM contract + quality leadership hiring = active decision-making on quality infrastructure.",
        supportingSignals: [MOCK_SIGNALS[5], MOCK_SIGNALS[6]],
        confidence: "high",
        nextStep:
          "High urgency. Reach out to incoming Quality Engineering Director or Manufacturing VP before headcount is in place.",
      },
    ],
    signals: [MOCK_SIGNALS[5], MOCK_SIGNALS[6]],
    fitRationaleSections: [
      {
        label: "Account Type Fit",
        fitLevel: "high",
        explanation: "Tier-2 precision machining supplier. Core ICP account type.",
      },
      {
        label: "Domain Fit",
        fitLevel: "high",
        explanation:
          "CNC machining of precision brackets and housings for automotive OEMs. Dimensional inspection is a standard quality requirement.",
      },
      {
        label: "Geography Fit",
        fitLevel: "high",
        explanation: "Brno, Czech Republic — within ICP target geography.",
      },
      {
        label: "Use Case Fit",
        fitLevel: "high",
        explanation:
          "Precision components with OEM PPM targets. New BMW programme contract creates immediate inspection demand.",
      },
      {
        label: "Process / System Fit",
        fitLevel: "medium",
        explanation: "Modern CNC environment with Heidenhain controllers. MES integration feasible.",
      },
      {
        label: "Ecosystem Role",
        fitLevel: "medium",
        explanation:
          "Tier-2 supplier with growing direct OEM exposure via BMW contract. Ecosystem influence increasing.",
      },
    ],
    ecosystem: [
      {
        relationship: "Customer",
        entity: "BMW Group",
        whyItMatters: "New direct OEM contract; PPM targets make inspection investment likely.",
      },
      {
        relationship: "Peer Supplier",
        entity: "Brno Engineering Works",
        whyItMatters: "Shares Czech manufacturing cluster; potential reference customer.",
      },
    ],
    decisions: [
      {
        date: "2026-04-14",
        actor: "agent",
        action: "Candidate Added",
        description:
          "Agent identified Volný as a candidate based on BMW contract and geography match.",
      },
    ],
  },
  {
    id: "m4",
    account: MOCK_ACCOUNTS[5], // Krantz
    streamId: "str1",
    status: "watching",
    fitScore: "medium",
    fitRationale:
      "Injection moulding supplier. Structural fit is medium — plastics inspection is a weaker use case for this product vs. metal stamping.",
    lastReviewed: "2026-04-08",
    angles: [],
    signals: [],
    fitRationaleSections: [
      {
        label: "Account Type Fit",
        fitLevel: "medium",
        explanation:
          "Tier-2 injection moulding supplier. Visual inspection applies but primary use case is metal components.",
      },
      {
        label: "Domain Fit",
        fitLevel: "medium",
        explanation:
          "Plastics inspection is a valid but secondary use case. Surface defect detection on plastic trim parts is possible.",
      },
      {
        label: "Geography Fit",
        fitLevel: "high",
        explanation: "Stuttgart, Germany — within ICP geography.",
      },
      {
        label: "Use Case Fit",
        fitLevel: "low",
        explanation:
          "Current product line focuses on interior plastic trim. Inspection needs are less urgent than metal stamping customers.",
      },
      {
        label: "Process / System Fit",
        fitLevel: "medium",
        explanation: "Injection moulding lines with basic quality sampling. MES integration limited.",
      },
      {
        label: "Ecosystem Role",
        fitLevel: "medium",
        explanation: "Supplies Bauer Automotive — indirect ecosystem link.",
      },
    ],
    ecosystem: [
      {
        relationship: "Customer",
        entity: "Bauer Automotive Systeme GmbH",
        whyItMatters: "Target account in this stream — shared ecosystem is a potential entry point.",
      },
    ],
    decisions: [
      {
        date: "2026-04-08",
        actor: "agent",
        action: "Candidate Added",
        description: "Added as medium-fit candidate. Placed in Active Watch pending signals.",
      },
    ],
  },
  {
    id: "m5",
    account: MOCK_ACCOUNTS[6], // Société Forgeage Laval
    streamId: "str1",
    status: "candidate",
    fitScore: "medium",
    fitRationale:
      "Forging supplier in France. Structural fit exists but signals not yet detected. Needs review.",
    angles: [],
    signals: [],
    fitRationaleSections: [
      {
        label: "Account Type Fit",
        fitLevel: "medium",
        explanation: "Tier-2 forging supplier. Forged components can require visual inspection.",
      },
      {
        label: "Domain Fit",
        fitLevel: "medium",
        explanation: "Forged automotive parts — surface inspection is applicable but less common.",
      },
      {
        label: "Geography Fit",
        fitLevel: "high",
        explanation: "Laval, France — within ICP geography.",
      },
      {
        label: "Use Case Fit",
        fitLevel: "medium",
        explanation:
          "Forged crankshafts and suspension components. Surface crack detection is a relevant use case.",
      },
      {
        label: "Process / System Fit",
        fitLevel: "low",
        explanation: "Legacy forge press environment. Digital integration capability unknown.",
      },
      {
        label: "Ecosystem Role",
        fitLevel: "low",
        explanation: "Small Tier-2 supplier. Limited ecosystem visibility.",
      },
    ],
    ecosystem: [],
    decisions: [
      {
        date: "2026-04-30",
        actor: "agent",
        action: "Candidate Added",
        description: "Identified as potential candidate based on geographic and domain match.",
      },
    ],
  },
  // Stream 2 – Predictive Maintenance × Aerospace Tier-1
  {
    id: "m6",
    account: MOCK_ACCOUNTS[3], // Airframe Solutions
    streamId: "str2",
    status: "watching",
    fitScore: "high",
    fitRationale:
      "Large aerostructure Tier-1 with active CBM programme launch. Near-perfect fit for predictive maintenance analytics.",
    lastReviewed: "2026-04-26",
    topAngle: {
      id: "ang4",
      title: "CBM Programme – Aerostructure Manufacturing Equipment",
      status: "actionable",
      rationale:
        "Airframe Solutions has launched an internal CBM digitisation programme and is hiring reliability engineers. Strong fit for the PdM analytics platform.",
      fitBasis: "Heavy manufacturing equipment in aerospace environment. AS9100 compliance pressure.",
      timingBasis: "Internal programme launched + hiring + MRO Europe speaking slot.",
      supportingSignals: [MOCK_SIGNALS[7], MOCK_SIGNALS[8], MOCK_SIGNALS[9]],
      confidence: "high",
      nextStep:
        "Target the newly posted Reliability Engineering leadership roles. Position as accelerator for their existing CBM programme.",
    },
    angles: [
      {
        id: "ang4",
        title: "CBM Programme – Aerostructure Manufacturing Equipment",
        status: "actionable",
        rationale:
          "Airframe Solutions has launched an internal CBM digitisation programme and is hiring reliability engineers.",
        fitBasis:
          "Heavy manufacturing equipment in aerospace environment with AS9100 compliance pressure.",
        timingBasis: "Internal CBM programme + reliability hiring + MRO Europe conference slot.",
        supportingSignals: [MOCK_SIGNALS[7], MOCK_SIGNALS[8], MOCK_SIGNALS[9]],
        confidence: "high",
        nextStep:
          "Target newly posted Reliability Engineering roles. Position platform as CBM programme accelerator.",
      },
    ],
    signals: [MOCK_SIGNALS[7], MOCK_SIGNALS[8], MOCK_SIGNALS[9]],
    fitRationaleSections: [
      {
        label: "Account Type Fit",
        fitLevel: "high",
        explanation:
          "Tier-1 aerostructure manufacturer. Primary ICP account type for predictive maintenance.",
      },
      {
        label: "Domain Fit",
        fitLevel: "high",
        explanation:
          "Aerospace manufacturing involves precision CNC and forming equipment where unplanned downtime has severe cost and programme implications.",
      },
      {
        label: "Geography Fit",
        fitLevel: "high",
        explanation: "Toulouse and Bremen facilities. Both within ICP geography.",
      },
      {
        label: "Use Case Fit",
        fitLevel: "high",
        explanation:
          "Digitising maintenance records and moving to CBM is explicitly the stated programme objective — exactly the user product's value proposition.",
      },
      {
        label: "Process / System Fit",
        fitLevel: "high",
        explanation:
          "Likely OPC-UA capable CNCs and SCADA infrastructure at Toulouse. Integration pathway exists.",
      },
      {
        label: "Ecosystem Role",
        fitLevel: "high",
        explanation:
          "Tier-1 to Airbus. AS9100 compliance environment with high maintenance cost pressure.",
      },
    ],
    ecosystem: [
      {
        relationship: "Customer",
        entity: "Airbus",
        whyItMatters: "Primary customer. Airbus programme pressure drives quality and reliability investment.",
      },
      {
        relationship: "Peer",
        entity: "Engelhoff Composite Technologies AG",
        whyItMatters: "Shares Airbus supplier ecosystem — potential co-reference.",
      },
    ],
    decisions: [
      {
        date: "2026-04-12",
        actor: "agent",
        action: "Candidate Added",
        description: "Identified as high-fit candidate for predictive maintenance stream.",
      },
      {
        date: "2026-04-13",
        actor: "user",
        action: "Accepted as Target Account",
        description: "User confirmed relevance. Placed in Active Watch.",
      },
      {
        date: "2026-04-26",
        actor: "agent",
        action: "Trigger Detected",
        description: "CBM programme announcement and reliability hiring signals detected.",
      },
    ],
  },
  {
    id: "m7",
    account: MOCK_ACCOUNTS[4], // Engelhoff
    streamId: "str2",
    status: "watching",
    fitScore: "high",
    fitRationale:
      "Composite Tier-1 manufacturer. Strong structural fit but no active timing signals detected yet.",
    lastReviewed: "2026-04-15",
    topAngle: {
      id: "ang5",
      title: "AS9100 Compliance – Digital Maintenance Records",
      status: "watch",
      rationale:
        "AS9100 recertification audit scope expansion is a potential trigger for maintenance digitalisation investment.",
      fitBasis: "Aerospace composite manufacturer with complex equipment maintenance needs.",
      timingBasis: "AS9100 audit scope expansion — low urgency but relevant.",
      supportingSignals: [MOCK_SIGNALS[10], MOCK_SIGNALS[11]],
      confidence: "medium",
      nextStep: "Monitor for additional programme or CapEx signals before acting.",
    },
    angles: [
      {
        id: "ang5",
        title: "AS9100 Compliance – Digital Maintenance Records",
        status: "watch",
        rationale:
          "AS9100 recertification audit scope expansion is a potential trigger for maintenance digitalisation investment.",
        fitBasis: "Aerospace composite manufacturer with complex equipment maintenance needs.",
        timingBasis: "AS9100 audit scope expansion signals possible compliance-driven investment.",
        supportingSignals: [MOCK_SIGNALS[10], MOCK_SIGNALS[11]],
        confidence: "medium",
        nextStep: "Monitor for additional programme or CapEx signals before acting.",
      },
    ],
    signals: [MOCK_SIGNALS[10], MOCK_SIGNALS[11]],
    fitRationaleSections: [
      {
        label: "Account Type Fit",
        fitLevel: "high",
        explanation: "Tier-1 aerospace composite manufacturer. Within ICP target account types.",
      },
      {
        label: "Domain Fit",
        fitLevel: "high",
        explanation:
          "Composite layup and autoclave equipment requires condition-based monitoring to prevent programme delays.",
      },
      {
        label: "Geography Fit",
        fitLevel: "high",
        explanation: "Hamburg, Germany — within ICP geography.",
      },
      {
        label: "Use Case Fit",
        fitLevel: "medium",
        explanation:
          "Autoclave and CNC trimming equipment maintenance. CBM applies but current maturity unclear.",
      },
      {
        label: "Process / System Fit",
        fitLevel: "medium",
        explanation:
          "Likely modern CNC environment. AS9100 compliance requires maintenance records which enables digitisation.",
      },
      {
        label: "Ecosystem Role",
        fitLevel: "high",
        explanation: "Tier-1 to Airbus and Rolls-Royce. High compliance and reliability pressure.",
      },
    ],
    ecosystem: [
      {
        relationship: "Customer",
        entity: "Airbus",
        whyItMatters: "Primary OEM customer. AS9100 compliance pressure drives maintenance investment.",
      },
      {
        relationship: "Customer",
        entity: "Rolls-Royce",
        whyItMatters: "Secondary customer. Engine nacelle composite components.",
      },
    ],
    decisions: [
      {
        date: "2026-04-08",
        actor: "agent",
        action: "Candidate Added",
        description: "Identified as high-fit aerospace candidate.",
      },
      {
        date: "2026-04-10",
        actor: "user",
        action: "Accepted as Target Account",
        description: "Confirmed and placed in Active Watch. No immediate signals detected.",
      },
    ],
  },
];

// ─── Streams ───────────────────────────────────────────────────────────────────

export const MOCK_STREAMS: Stream[] = [
  {
    id: "str1",
    product: MOCK_PRODUCTS[0],
    icp: MOCK_ICPS[0],
    status: "idle",
    accountCount: 42,
    lastUpdated: "2026-05-02T14:32:00Z",
    agentConfidence: "high",
    statusCounts: {
      candidate: 12,
      watching: 21,
      engaged: 4,
      deprioritized: 5,
    },
  },
  {
    id: "str2",
    product: MOCK_PRODUCTS[1],
    icp: MOCK_ICPS[1],
    status: "running",
    accountCount: 28,
    lastUpdated: "2026-05-03T09:15:00Z",
    agentConfidence: "medium",
    statusCounts: {
      candidate: 8,
      watching: 14,
      engaged: 2,
      deprioritized: 4,
    },
  },
];

// ─── Review Items ─────────────────────────────────────────────────────────────

export const MOCK_REVIEW_ITEMS: ReviewItem[] = [
  {
    id: "r1",
    type: "state_change",
    account: MOCK_ACCOUNTS[2], // Volný Precision
    stream: MOCK_STREAMS[0],
    reason:
      "Volný Precision has high account fit and two strong timing signals: a new BMW OEM contract with strict PPM targets and a Quality Engineering Director hire.",
    evidence: [
      "Quality Engineering Director role posted 2026-04-30",
      "BMW iDrive programme contract awarded with PPM targets cited",
    ],
    confidence: "high",
  },
  {
    id: "r2",
    type: "new_candidate",
    account: MOCK_ACCOUNTS[6], // Société Forgeage Laval
    stream: MOCK_STREAMS[0],
    reason:
      "Agent identified Société Forgeage Laval as a potential candidate based on ICP geography and domain match. Confidence is medium — structural fit is lower than primary targets.",
    evidence: [
      "Automotive forging supplier in France (within ICP geography)",
      "Produces crankshafts and suspension components requiring surface inspection",
    ],
    confidence: "medium",
  },
  {
    id: "r3",
    type: "new_angle",
    account: MOCK_ACCOUNTS[1], // Groupe Mercier
    stream: MOCK_STREAMS[0],
    reason:
      "Agent identified a new opportunity angle: Mercier's Polish greenfield plant is a clean-slate quality infrastructure decision — ideal timing for embedded inspection technology.",
    evidence: [
      "12,000 m² greenfield plant in Lodz confirmed",
      "Production targeting EV battery brackets — new quality requirements",
      "KUKA robotics partnership creates integration pathway",
    ],
    confidence: "medium",
  },
  {
    id: "r4",
    type: "state_change",
    account: MOCK_ACCOUNTS[3], // Airframe Solutions
    stream: MOCK_STREAMS[1],
    reason:
      "Airframe Solutions has launched an internal CBM programme and is actively hiring reliability engineers.",
    evidence: [
      "MRO digitisation programme officially announced",
      "5 Reliability Engineer roles posted at Toulouse and Bremen",
      "Speaking slot at MRO Europe on CBM strategies",
    ],
    confidence: "high",
  },
  {
    id: "r5",
    type: "signal_cluster",
    account: MOCK_ACCOUNTS[4], // Engelhoff
    stream: MOCK_STREAMS[1],
    reason:
      "Two signals detected for Engelhoff Composite Technologies: AS9100 audit scope expansion and €8M quality lab investment.",
    evidence: [
      "AS9100 Rev D audit scope expanded to include digital maintenance records",
      "€8M capex approved for quality lab upgrades",
    ],
    confidence: "medium",
  },
];

// ─── Activity Feed ─────────────────────────────────────────────────────────────

export const MOCK_ACTIVITY_FEED: ActivityFeedItem[] = [
  {
    id: "af1",
    date: "2026-05-03T09:15:00Z",
    accountName: "Airframe Solutions SAS",
    accountId: "a4",
    streamId: "str2",
    description: "New reliability engineering hiring signal detected.",
    type: "signal_detected",
  },
  {
    id: "af2",
    date: "2026-05-02T14:32:00Z",
    accountName: "Bauer Automotive Systeme GmbH",
    accountId: "a1",
    streamId: "str1",
    description: "Account promoted to Engagement Window.",
    type: "user_status_change",
  },
  {
    id: "af3",
    date: "2026-04-30T11:20:00Z",
    accountName: "Société Forgeage Laval",
    accountId: "a7",
    streamId: "str1",
    description: "Agent added as candidate based on geography and domain match. Awaiting review.",
    type: "new_candidate",
    requiresDecision: true,
  },
  {
    id: "af4",
    date: "2026-04-28T08:45:00Z",
    accountName: "Volný Precision Components s.r.o.",
    accountId: "a3",
    streamId: "str1",
    description: "BMW contract and quality hiring signals detected. State changed to Trigger Detected.",
    type: "user_status_change",
  },
  {
    id: "af5",
    date: "2026-04-26T15:10:00Z",
    accountName: "Airframe Solutions SAS",
    accountId: "a4",
    streamId: "str2",
    description: "State changed to Trigger Detected after CBM programme announcement.",
    type: "user_status_change",
  },
  {
    id: "af6",
    date: "2026-04-20T10:00:00Z",
    accountName: "Groupe Mercier Industries",
    accountId: "a2",
    streamId: "str1",
    description: "Opportunity angle identified: EV Battery Component Line quality at scale.",
    type: "opportunity_identified",
  },
  {
    id: "af7",
    date: "2026-04-18T11:30:00Z",
    accountName: "Volný Precision Components s.r.o.",
    accountId: "a3",
    streamId: "str1",
    description: "New OEM contract signal detected — BMW iDrive programme with strict PPM targets.",
    type: "signal_detected",
  },
  {
    id: "af8",
    date: "2026-04-15T09:00:00Z",
    accountName: "Bauer Automotive Systeme GmbH",
    accountId: "a1",
    streamId: "str1",
    description: "€45M CapEx investment in new stamping line confirmed in annual report.",
    type: "signal_detected",
  },
  {
    id: "af9",
    date: "2026-04-13T14:20:00Z",
    accountName: "Airframe Solutions SAS",
    accountId: "a4",
    streamId: "str2",
    description: "User accepted account as actively monitored.",
    type: "decision_made",
  },
  {
    id: "af10",
    date: "2026-04-12T10:00:00Z",
    accountName: "Bauer Automotive Systeme GmbH",
    accountId: "a1",
    streamId: "str1",
    description: "User confirmed account as Target Account.",
    type: "decision_made",
  },
  {
    id: "af11",
    date: "2026-04-10T16:00:00Z",
    accountName: "Société Forgeage Laval",
    accountId: "a7",
    streamId: "str1",
    description: "Agent identified as potential candidate based on geography and domain match. Awaiting review.",
    type: "new_candidate",
    requiresDecision: true,
  },
  {
    id: "af12",
    date: "2026-04-08T09:45:00Z",
    accountName: "Engelhoff Composite Technologies AG",
    accountId: "a5",
    streamId: "str2",
    description: "AS9100 audit scope expansion signal detected — potential maintenance digitalisation trigger.",
    type: "signal_detected",
  },
  {
    id: "af13",
    date: "2026-04-05T11:00:00Z",
    accountName: "Airframe Solutions SAS",
    accountId: "a4",
    streamId: "str2",
    description: "Opportunity angle identified: CBM Programme for aerostructure manufacturing equipment.",
    type: "opportunity_identified",
  },
  {
    id: "af14",
    date: "2026-04-02T08:30:00Z",
    accountName: "Groupe Mercier Industries",
    accountId: "a2",
    streamId: "str1",
    description: "KUKA Robotics partnership announced — new automation integration signal.",
    type: "signal_detected",
  },
  {
    id: "af15",
    date: "2026-03-28T14:00:00Z",
    accountName: "Krantz Automotive Plastics GmbH",
    accountId: "a6",
    streamId: "str1",
    description: "Agent added as medium-fit candidate. Awaiting your review.",
    type: "new_candidate",
    requiresDecision: true,
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function getMatchesForStream(streamId: string): AccountStreamMatch[] {
  return MOCK_ACCOUNT_STREAM_MATCHES.filter((m) => m.streamId === streamId);
}

export function getMatchById(id: string): AccountStreamMatch | undefined {
  return MOCK_ACCOUNT_STREAM_MATCHES.find((m) => m.id === id);
}

export function getMatchByAccountAndStream(
  accountId: string,
  streamId: string,
): AccountStreamMatch | undefined {
  return MOCK_ACCOUNT_STREAM_MATCHES.find(
    (m) => m.account.id === accountId && m.streamId === streamId,
  );
}

export function getStreamById(id: string): Stream | undefined {
  return MOCK_STREAMS.find((s) => s.id === id);
}

export function getICPLabel(streamId: string): string {
  const s = MOCK_STREAMS.find((st) => st.id === streamId);
  return s ? s.icp.name : streamId;
}

export function getICPById(streamId: string): ICP | undefined {
  const s = MOCK_STREAMS.find((st) => st.id === streamId);
  return s?.icp;
}
